// ─────────────────────────────────────────────────────────────────────────────
// src/db/cacheService.js
// Descarga y cachea productos y clientes en IndexedDB cuando hay internet.
// El POS los usa automáticamente cuando está offline.
// ─────────────────────────────────────────────────────────────────────────────

import { cacheProducts, cacheCustomers } from './localDB';
import { productsAPI, customersAPI, sucursalesAPI } from '../services/api';

/**
 * Actualiza el caché local de productos y clientes del usuario.
 * Llamar esto cuando el usuario carga la app estando online.
 *
 * @param {number} userId - ID del usuario autenticado
 * @param {number|null} sucursalId - Sucursal activa del dispositivo. Si viene,
 *   se cachea el precio EFECTIVO de esa sucursal (especial si existe, general
 *   si no) — así el modo offline muestra el mismo precio que el modo online,
 *   en vez de caer siempre al precio general del catálogo.
 * @returns {{ ok: boolean, error?: string }}
 */
export async function refreshOfflineCache(userId, sucursalId = null) {
  if (!userId) return { ok: false, error: 'Sin userId' };
  if (!navigator.onLine) return { ok: false, error: 'Sin conexion' };

  try {
    const productsPromise = sucursalId
      ? sucursalesAPI.getProductos(sucursalId).then(res =>
          (res.data?.data?.productos || []).map(p => ({
            id: p.producto_id,
            code: p.code,
            name: p.name,
            category: p.category,
            unit: p.unit,
            purchase_price: p.purchase_price,
            sale_price: p.precio_efectivo,
            stock: p.stock_general,
            min_stock: p.min_stock,
            is_active: p.is_active,
          }))
        )
      : productsAPI.getAll('active').then(res => {
          const raw = res.data?.data;
          return Array.isArray(raw) ? raw : (raw?.products || []);
        });

    // Descargar productos (per-sucursal o global) y clientes en paralelo
    const [products, customersRes] = await Promise.all([
      productsPromise,
      customersAPI.getAll()
    ]);

    // El backend responde: { success, data: { customers: [...] } }
    const raw2      = customersRes.data?.data;
    const customers = Array.isArray(raw2) ? raw2 : (raw2?.customers || []);

    // Guardar en IndexedDB
    await cacheProducts(products, userId);
    await cacheCustomers(customers, userId);

    console.log(`[Cache] ${products.length} productos y ${customers.length} clientes cacheados offline.`);
    return { ok: true };

  } catch (error) {
    console.error('[Cache] Error al actualizar caché offline:', error.message);
    return { ok: false, error: error.message };
  }
}
